import { useEffect, useMemo, useRef } from "react";
import { Item } from "types";
import { useItems } from "atoms/itemAtom";

import { Divider } from "components/Divider/Divider";

import styles from "../styles.module.scss";

import { ProductsHeader, ProductItem } from "./index";

/** Порог (px до конца скролла): подгрузка только когда до конца осталось не меньше */
const SCROLL_END_THRESHOLD = 400;

function getScrollParent(el: Element | null): Element | null {
  if (!el) return null;
  let parent = el.parentElement;
  while (parent) {
    const { overflowY } = getComputedStyle(parent);
    if (/(auto|scroll|overlay)/.test(overflowY)) return parent;
    parent = parent.parentElement;
  }
  return null;
}

type ProductsListProps = {
  items: Item[];
  onScrollEnd?: () => void;
  hasNextPage?: boolean;
  isLoadingMore?: boolean;
};

export const ProductsList = ({
  items,
  onScrollEnd,
  hasNextPage,
  isLoadingMore,
}: ProductsListProps): JSX.Element => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const isLoadingRef = useRef(false);
  const { itemsState, setItemsState } = useItems();

  const sortedProducts = useMemo(
    () => [...items].sort((a, b) => (a.position > b.position ? 1 : -1)),
    [items]
  );

  useEffect(() => {
    isLoadingRef.current = Boolean(isLoadingMore);
  }, [isLoadingMore]);

  useEffect(() => {
    if (itemsState.checkedItems.length) {
      setItemsState({
        checkedItems: sortedProducts.filter((temp) =>
          itemsState.checkedItems.some(
            (tempItem) => tempItem.itemid === temp.itemid
          )
        ),
      });
    }
  }, [sortedProducts]);

  useEffect(() => {
    if (!onScrollEnd || !hasNextPage || isLoadingMore) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const root = getScrollParent(sentinel) ?? null;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        if (isLoadingRef.current) return;
        isLoadingRef.current = true;
        onScrollEnd();
      },
      {
        root,
        rootMargin: `0px 0px ${SCROLL_END_THRESHOLD}px 0px`,
        threshold: 0,
      }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [onScrollEnd, hasNextPage, isLoadingMore]);

  return (
    <div className={styles.productsList}>
      <ProductsHeader productsList={sortedProducts} />
      <Divider />
      {sortedProducts.map((item) => (
        <ProductItem item={item} key={item.itemid ?? Math.random() + "-"} />
      ))}
      {hasNextPage && <div ref={sentinelRef} className={styles.loadingSentinel} />}
      {isLoadingMore && (
        <div className={styles.loadingMore}>Загрузка...</div>
      )}
    </div>
  );
};
